// Player names and map titles: what's allowed, and the bad-word filter (the same code runs in the
// game and on the server). The filter sees through l33t speak, look-alike letters, repeated letters
// and characters put between letters ("f.u.c.k").

import {
  DataSet,
  RegExpMatcher,
  englishDataset,
  englishRecommendedBlacklistMatcherTransformers,
  englishRecommendedWhitelistMatcherTransformers,
  parseRawPattern,
  pattern,
  skipNonAlphabeticTransformer,
} from 'obscenity';

const dataset = new DataSet<{ originalWord: string }>().addAll(englishDataset);
// Danish words, and a few more the English list doesn't have. "|" = word boundary.
for (const w of [
  'fisse',
  'kusse',
  'luder',
  'kaelling',
  'perker',
  'neger',
  'pikhoved',
  'pikfjaes',
  'rovhul',
  'roevhul',
  'bosserov',
  'spasser',
  'hitler',
  'nazi',
])
  dataset.addPhrase((p) => p.setMetadata({ originalWord: w }).addPattern(parseRawPattern(w)));
dataset.addPhrase((p) => p.setMetadata({ originalWord: 'pik' }).addPattern(pattern`|pik|`));
dataset.addPhrase((p) => p.setMetadata({ originalWord: 'lort' }).addPattern(pattern`|lort`));

const matcher = new RegExpMatcher({
  ...dataset.build(),
  // Also skip characters between letters, so "f.u.c.k" and "f_u_c_k" are caught.
  blacklistMatcherTransformers: [
    ...englishRecommendedBlacklistMatcherTransformers,
    skipNonAlphabeticTransformer(),
  ],
  whitelistMatcherTransformers: englishRecommendedWhitelistMatcherTransformers,
});

export function hasBadWords(text: string): boolean {
  return matcher.hasMatch(text);
}

export const NAME_MIN = 3;
export const NAME_MAX = 20;

/** Names nobody gets (they'd look official). */
const RESERVED = new Set([
  'admin',
  'administrator',
  'moderator',
  'mod',
  'staff',
  'system',
  'server',
  'owner',
  'official',
  'anonymous',
  'unknown',
  'null',
  'undefined',
  'bhopweb',
  'bhop web',
  'cssbhop',
  'css bhop',
  'css-bhop',
]);

/** Tidy a typed name: trimmed, single spaces. */
export function tidyName(name: string): string {
  return name.replace(/\s+/g, ' ').trim();
}

/** Why a name can't be used, or null if it's fine. Expects tidyName() output. */
export function nameProblem(name: string): string | null {
  if (name.length < NAME_MIN) return `At least ${NAME_MIN} characters.`;
  if (name.length > NAME_MAX) return `At most ${NAME_MAX} characters.`;
  if (!/^[A-Za-z0-9 _.-]+$/.test(name)) return 'Letters A–Z, digits, spaces, _ . and - only.';
  if (!/[A-Za-z0-9]/.test(name)) return 'Use at least one letter or digit.';
  if (RESERVED.has(name.toLowerCase())) return 'That name is reserved.';
  if (hasBadWords(name)) return "That name isn't allowed.";
  return null;
}

/** Why a map title can't be used, or null. */
export function titleProblem(title: string): string | null {
  if (title.length < 3) return 'Give the map a name of at least 3 characters.';
  if (hasBadWords(title)) return "That map name isn't allowed.";
  return null;
}
